import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Tab,
  Tabs,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  ShowChart as ShowChartIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import MockDataIndicator from '../../../../Components/Common/MockDataIndicator';
import {
  thaklisFinanceOverview,
  profitByService,
  profitByCustomer,
  getPendingCommissions,
  getTotalPendingCommissions,
  getPendingServicePayments,
  getTotalPendingServicePayments,
  gatewayAnalysis,
  serviceProfitabilityTrends,
  topPerformingServices,
  paymentMethodDistribution,
} from '../../../../mockData/financeData';

// Utility function to format currency
const formatCurrency = (amount, currency = 'AED') => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color = 'primary', trend, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: `${color}.main`, mb: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {trend > 0 ? (
                <TrendingUpIcon fontSize="small" color="success" />
              ) : (
                <TrendingDownIcon fontSize="small" color="error" />
              )}
              <Typography
                variant="caption"
                sx={{ ml: 0.5, color: trend > 0 ? 'success.main' : 'error.main' }}
              >
                {Math.abs(trend)}% vs last month
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 32, color: `${color}.main` }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Tab Panel Component
const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const ThaklisDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isMockData, setIsMockData] = useState(true);
  const [financeData, setFinanceData] = useState(null);

  useEffect(() => {
    // In production, this would be an API call
    // AppService.get('v1/finance/overview').then(...)

    // For now, using mock data
    setFinanceData(thaklisFinanceOverview);
    setIsMockData(true);
  }, []);

  if (!financeData) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading finance data...</Typography>
      </Box>
    );
  }

  const { currentMonth, monthlyTrend, walletBalances } = financeData;
  const pendingCommissions = getTotalPendingCommissions();
  const pendingServicePayments = getTotalPendingServicePayments();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Finance Analytics
        </Typography>
        <MockDataIndicator isMock={isMockData} />
      </Box>

      {isMockData && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Mock Data Mode:</strong> This page is displaying sample analytical data.
          Backend integration is pending.
        </Alert>
      )}

      {/* Tabs for Detailed Views */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Profit by Service" />
            <Tab label="Profit by Customer" />
            <Tab label="Monthly Trend" />
            <Tab label="Service Profitability Trends" />
            <Tab label="Top Performing Services" />
            <Tab label="Payment Method Distribution" />
            <Tab label="Gateway Analysis" />
          </Tabs>
        </Box>

        {/* Tab 1: Profit by Service */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Profit Breakdown by Service Type
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              {profitByService.map((service) => (
                <Grid item xs={12} key={service.serviceId}>
                  <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {service.serviceName}
                        </Typography>
                        <Chip label={`${service.totalTickets} tickets`} size="small" color="primary" />
                      </Box>

                      <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Revenue
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            {formatCurrency(service.totalRevenue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Gross Profit
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '0.875rem' }}>
                            {formatCurrency(service.grossProfit)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Commissions
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main', fontSize: '0.875rem' }}>
                            {formatCurrency(service.totalCommissions)}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Net Profit
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.875rem' }}>
                            {formatCurrency(service.netProfit)}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Profit Margin
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(service.grossProfit / service.totalRevenue) * 100}
                            sx={{ flexGrow: 1, mr: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: '35px' }}>
                            {((service.grossProfit / service.totalRevenue) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 2: Profit by Customer */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Profit Breakdown by Customer
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              {profitByCustomer.map((customer) => (
                <Grid item xs={12} md={6} key={customer.customerId}>
                  <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.8 }}>
                        {customer.customerName}
                      </Typography>

                      {customer.channelPartner && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8, fontSize: '0.7rem' }}>
                          Referred by: <strong>{customer.channelPartner}</strong>
                        </Typography>
                      )}

                      <Grid container spacing={0.8}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Tickets
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{customer.totalTickets}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Revenue
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{formatCurrency(customer.totalRevenue)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Gross Profit
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'success.main', fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {formatCurrency(customer.grossProfit)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Net Profit
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'info.main', fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {formatCurrency(customer.netProfit)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {customer.channelPartner && (
                        <Box sx={{ mt: 0.8, pt: 0.8, borderTop: '1px solid #eee' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Commissions Paid: <strong>{formatCurrency(customer.commissionsPaid)}</strong>
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 3: Monthly Trend */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
              6-Month Financial Trend
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              {monthlyTrend.map((month, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold', mb: 0.8 }}>
                        {month.month}
                      </Typography>

                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Revenue</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                            {formatCurrency(month.revenue)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Gross Profit</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '0.7rem' }}>
                            {formatCurrency(month.grossProfit)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Net Profit</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.7rem' }}>
                            {formatCurrency(month.netProfit)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 4: Service Profitability Trends */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Service Profitability Trends (6 Months)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              {serviceProfitabilityTrends.map((monthData, index) => (
                <Grid item xs={12} key={index}>
                  <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                        {monthData.month}
                      </Typography>

                      <Grid container spacing={1}>
                        {monthData.services.map((service, sIndex) => (
                          <Grid item xs={12} sm={6} md={3} key={sIndex}>
                            <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                {service.serviceName}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Revenue:</Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                  {formatCurrency(service.revenue)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Profit:</Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: 'success.main' }}>
                                  {formatCurrency(service.grossProfit)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Tickets:</Typography>
                                <Chip label={service.tickets} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 5: Top Performing Services */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Top Performing Services (Current Month)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              {topPerformingServices.map((service) => (
                <Grid item xs={12} md={6} key={service.rank}>
                  <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          #{service.rank} {service.serviceName}
                        </Typography>
                        <Chip
                          label={service.growth >= 0 ? `+${service.growth}%` : `${service.growth}%`}
                          size="small"
                          color={service.growth >= 0 ? 'success' : 'error'}
                        />
                      </Box>

                      <Grid container spacing={0.8}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Tickets
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {service.totalTickets}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Revenue
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {formatCurrency(service.totalRevenue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Profit
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'success.main' }}>
                            {formatCurrency(service.grossProfit)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Margin
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'info.main' }}>
                            {service.profitMargin}%
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Avg/Ticket
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {formatCurrency(service.avgTicketValue)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 6: Payment Method Distribution */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Payment Method Distribution
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              {paymentMethodDistribution.map((method, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {method.method}
                        </Typography>
                        <Chip
                          label={method.category}
                          size="small"
                          color={method.category === 'ONLINE' ? 'primary' : 'warning'}
                        />
                      </Box>

                      <Grid container spacing={0.8}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Transactions
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {method.totalTransactions}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Total Amount
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {formatCurrency(method.totalAmount)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Percentage
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                            <LinearProgress
                              variant="determinate"
                              value={method.percentage}
                              sx={{ flexGrow: 1, mr: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: '35px' }}>
                              {method.percentage}%
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Avg Transaction
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'info.main' }}>
                            {formatCurrency(method.avgTransactionValue)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Total Fees
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'error.main' }}>
                            {formatCurrency(method.fees)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Net Amount
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'success.main' }}>
                            {formatCurrency(method.netAmount)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 7: Gateway Analysis */}
        <TabPanel value={tabValue} index={6}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Payment Gateway Cost Analysis
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={1.5}>
              {gatewayAnalysis.map((gateway, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.8 }}>
                        {gateway.gatewayName}
                      </Typography>

                      <Grid container spacing={0.8}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Transactions
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{gateway.totalTransactions}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Volume
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{formatCurrency(gateway.totalVolume)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Total Fees
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'error.main', fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {formatCurrency(gateway.totalFees)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Avg Fee %
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>{gateway.avgFeePercentage}%</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ThaklisDashboard;
